import { useState, type ReactNode } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ChevronDown,
  Pencil,
  Trash2,
  Loader2,
  ListChecks,
  MessageSquare,
  Share2,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { useDirtyClose } from '@/hooks/use-dirty-close'
import { BoatForm, type BoatFormValues } from '@/features/inventory/boat-form'
import { BoatImageGallery } from '@/features/inventory/components/boat-image-gallery'
import {
  useBoat,
  useUpdateBoat,
  useDeleteBoat,
  useSlipForBoat,
  useWorkOrdersForBoat,
} from '@/features/inventory/hooks'
import { useSchedules, usePlanSuggestion, useCreateSchedule } from '@/features/service/hooks'
import { ApplyPlanDialog } from '@/features/service/apply-plan-dialog'
import { ScheduleForm } from '@/features/service/schedule-form'
import { ScheduleList } from '@/features/service/schedule-list'
import { MessagesSidebar } from '@/features/messaging/components/messages-sidebar'
import { PostBoatDialog } from '@/features/integrations/components/post-boat-dialog'
import { useCustomer } from '@/features/customers/hooks'
import { customerDisplayName } from '@/features/customers/customer-utils'
import { BoatConsignmentCard } from '@/features/consignment/components/boat-consignment-card'
import { BoatLoanCard } from '@/features/inventory/components/boat-loan-card'
import { requireFlow } from '@/features/auth/guards'
import { BOAT_STATUS_LABELS, LISTING_STATE_LABELS } from '@/types/inventory'
import { SLIP_TYPE_LABELS, type SlipType } from '@/types/storage'
import { WORK_ORDER_STATUS_LABELS, type WorkOrderStatus } from '@/types/service'

export const Route = createFileRoute('/_authenticated/inventory/$boatId')({
  beforeLoad: requireFlow('inventory'),
  component: BoatDetailPage,
})

const ENGINE_LABELS: Record<string, string> = {
  mercury: 'Mercury / MerCruiser',
  yamaha: 'Yamaha',
  honda: 'Honda',
  suzuki: 'Suzuki',
  volvo_penta: 'Volvo Penta',
  evinrude: 'Evinrude (E-TEC)',
  cmd: 'Cummins MerCruiser Diesel',
  indmar: 'Indmar',
  pcm: 'PCM',
  crusader: 'Crusader',
  yanmar: 'Yanmar',
}

// Compact label/value row (same idiom as the consignment detail page).
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right">{children}</span>
    </div>
  )
}

// Uppercase-label spec cell (same idiom as the deal overview grid).
function Spec({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{children}</div>
    </div>
  )
}

function BoatDetailPage() {
  const { boatId } = Route.useParams()
  const { data: boat, isLoading, isError } = useBoat(boatId)
  const { data: owner } = useCustomer(boat?.customer_id ?? '')
  const { data: slip, isLoading: slipLoading } = useSlipForBoat(boatId)
  const { data: workOrders = [] } = useWorkOrdersForBoat(boatId)
  const { data: schedules = [], isLoading: schedulesLoading } = useSchedules({ boat_id: boatId })
  const { data: suggestion } = usePlanSuggestion(boatId)

  const navigate = useNavigate()
  const updateMutation = useUpdateBoat()
  const deleteMutation = useDeleteBoat()
  const createScheduleMutation = useCreateSchedule()
  const [editOpen, setEditOpen] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const { guardOpenChange: guardEditOpen, discardGuard: editDirtyGuard } = useDirtyClose(
    editOpen,
    setEditOpen,
    formDirty,
  )
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [markSoldOpen, setMarkSoldOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)
  const [postOpen, setPostOpen] = useState(false)
  const [applyPlanOpen, setApplyPlanOpen] = useState(false)
  const [addScheduleOpen, setAddScheduleOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [planOpen, setPlanOpen] = useState(false)

  const engineBody = (values: BoatFormValues) => ({
    engine_manufacturer:
      values.engine_manufacturer === 'none' || !values.engine_manufacturer
        ? ''
        : values.engine_manufacturer,
    engine_model: values.engine_model || '',
    engine_count: values.engine_count ?? 1,
    engine_serial: values.engine_serial || '',
    engine_hours: values.engine_hours === '' ? null : (values.engine_hours ?? null),
    hin: values.hin || '',
  })

  const handleUpdate = (values: BoatFormValues) => {
    updateMutation.mutate(
      {
        id: boatId,
        body: {
          make: values.make,
          model: values.model,
          year: values.year,
          price: values.price,
          cost: values.cost,
          length_ft: values.length_ft,
          status: values.status,
          listing_state: values.listing_state,
          condition: values.condition,
          type: values.type,
          color: values.color || '',
          description: values.description || '',
          customer_id: values.customer_id || undefined,
          ...engineBody(values),
        },
      },
      { onSuccess: () => setEditOpen(false) },
    )
  }

  const handleMarkAsSold = () => {
    if (!boat) return
    updateMutation.mutate(
      {
        id: boatId,
        body: {
          make: boat.make,
          model: boat.model,
          year: boat.year,
          price: boat.price,
          cost: boat.cost,
          length_ft: boat.length_ft,
          status: boat.status,
          listing_state: 'sold',
          condition: boat.condition,
          color: boat.color,
          description: boat.description,
          customer_id: boat.customer_id ?? undefined,
          engine_manufacturer: boat.engine_manufacturer,
          engine_model: boat.engine_model,
          engine_count: boat.engine_count,
          engine_serial: boat.engine_serial,
          engine_hours: boat.engine_hours ?? null,
          hin: boat.hin,
        },
      },
      { onSuccess: () => setMarkSoldOpen(false) },
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(boatId, {
      onSuccess: () => navigate({ to: '/inventory' }),
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
            <Skeleton className="h-96 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (isError || !boat) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive">Boat not found.</p>
        <Link to="/inventory">
          <Button variant="outline">Back to Inventory</Button>
        </Link>
      </div>
    )
  }

  const openWorkOrders = workOrders.filter(
    (wo) => wo.status !== 'completed' && wo.status !== 'canceled',
  ).length

  const engineLabel = [
    boat.engine_manufacturer
      ? (ENGINE_LABELS[boat.engine_manufacturer] ?? boat.engine_manufacturer)
      : null,
    boat.engine_model || null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex flex-col gap-6">
      <Link to="/inventory">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Inventory
        </Button>
      </Link>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="break-words text-3xl font-bold tracking-tight">
            {boat.make} {boat.model}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {boat.year} · {boat.length_ft} ft · ${boat.price.toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center sm:justify-end">
          <Badge variant="outline" className="text-md capitalize">
            {boat.condition}
          </Badge>
          <Badge variant="outline" className="text-md">
            {LISTING_STATE_LABELS[boat.listing_state]}
          </Badge>
          <Badge variant="secondary" className="text-md">
            {BOAT_STATUS_LABELS[boat.status]}
          </Badge>
          {boat.customer_id && (
            <>
              <Button variant="outline" size="sm" onClick={() => setMessageOpen(true)}>
                <MessageSquare className="mr-2 h-4 w-4" /> Messages
              </Button>
              <MessagesSidebar
                open={messageOpen}
                onOpenChange={setMessageOpen}
                customerId={boat.customer_id}
                customerName={owner ? customerDisplayName(owner) : undefined}
              />
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => setPostOpen(true)}>
            <Share2 className="mr-2 h-4 w-4" /> Post
          </Button>
          <PostBoatDialog
            open={postOpen}
            onOpenChange={setPostOpen}
            boatId={boatId}
            boatLabel={`${boat.year} ${boat.make} ${boat.model}`}
            price={`$${boat.price.toLocaleString()}`}
          />
          {boat.listing_state !== 'sold' && (
            <Dialog open={markSoldOpen} onOpenChange={setMarkSoldOpen}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setMarkSoldOpen(true)}
                disabled={updateMutation.isPending}
              >
                Mark as Sold
              </Button>
              <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                  <DialogTitle>
                    Mark {boat.make} {boat.model} as sold?
                  </DialogTitle>
                  <DialogDescription>
                    The listing state changes to Sold and the boat leaves the for-sale inventory.
                    You can change it back later from Edit.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMarkSoldOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleMarkAsSold} disabled={updateMutation.isPending}>
                    {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Mark as Sold
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={editOpen} onOpenChange={guardEditOpen}>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Boat</DialogTitle>
                <DialogDescription>Update this boat's details.</DialogDescription>
              </DialogHeader>
              <BoatForm
                onDirtyChange={setFormDirty}
                defaultValues={{
                  make: boat.make,
                  model: boat.model,
                  year: boat.year,
                  price: boat.price,
                  cost: boat.cost,
                  length_ft: boat.length_ft,
                  status: boat.status,
                  listing_state: boat.listing_state,
                  condition: boat.condition,
                  type: boat.type ?? 'other',
                  color: boat.color || '',
                  description: boat.description || '',
                  engine_manufacturer: boat.engine_manufacturer || '',
                  engine_model: boat.engine_model || '',
                  engine_count: boat.engine_count,
                  engine_serial: boat.engine_serial || '',
                  engine_hours: boat.engine_hours ?? '',
                  hin: boat.hin || '',
                  customer_id: boat.customer_id ?? '',
                }}
                onSubmit={handleUpdate}
                isPending={updateMutation.isPending}
                submitLabel="Save Changes"
              />
            </DialogContent>
          </Dialog>
          {editDirtyGuard}
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete boat?</DialogTitle>
                <DialogDescription>
                  This permanently deletes {boat.make} {boat.model} and cascades to its work orders.
                  This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <BoatImageGallery boatId={boatId} />

          {boat.description && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{boat.description}</p>
              </CardContent>
            </Card>
          )}

          {workOrders.length > 0 && (
            <Card>
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-expanded={historyOpen}
                className="flex w-full items-center justify-between gap-2 p-6 pb-2 text-left"
              >
                <span className="min-w-0">
                  <CardTitle>Service History</CardTitle>
                  <CardDescription className="mt-1.5">
                    {openWorkOrders} open · {workOrders.length} total
                  </CardDescription>
                </span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
                    historyOpen ? 'rotate-0' : '-rotate-90'
                  }`}
                />
              </button>
              {historyOpen && (
                <CardContent className="flex flex-col gap-2">
                  {workOrders.slice(0, 5).map((wo) => (
                    <Link
                      key={wo.id}
                      to="/service/$workOrderId"
                      params={{ workOrderId: wo.id }}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 hover:bg-accent"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{wo.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {wo.assigned_technician
                            ? `Assigned to ${wo.assigned_technician}`
                            : 'Unassigned'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">
                          {wo.priority}
                        </Badge>
                        <Badge variant="secondary">
                          {WORK_ORDER_STATUS_LABELS[wo.status as WorkOrderStatus]}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
              <button
                type="button"
                onClick={() => setPlanOpen((v) => !v)}
                aria-expanded={planOpen}
                className="flex min-w-0 flex-1 items-center text-left"
              >
                <span className="min-w-0">
                  <CardTitle>Service Plan</CardTitle>
                  <CardDescription className="mt-1.5">
                    Recurring service schedules for this boat. The owner is texted when a task
                    enters its reminder window.
                  </CardDescription>
                </span>
              </button>
              {planOpen && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAddScheduleOpen(true)}>
                    New Service Schedule
                  </Button>
                  <Button size="sm" className="gap-2" onClick={() => setApplyPlanOpen(true)}>
                    <ListChecks className="h-4 w-4" /> Apply manufacturer plan
                  </Button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setPlanOpen((v) => !v)}
                aria-expanded={planOpen}
                aria-label={planOpen ? 'Collapse Service Plan' : 'Expand Service Plan'}
                className="shrink-0 text-muted-foreground"
              >
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${planOpen ? 'rotate-0' : '-rotate-90'}`}
                />
              </button>
            </CardHeader>
            {planOpen && (
              <CardContent>
                <ScheduleList
                  schedules={schedules}
                  isLoading={schedulesLoading}
                  emptyMessage="No schedules yet — apply a manufacturer plan to get started."
                />
              </CardContent>
            )}
          </Card>
        </div>

        {/* Right rail */}
        <div className="flex min-w-0 flex-col gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {owner && (
                <Row label="Owner">
                  <span className="inline-flex flex-col items-end">
                    <Link
                      to="/sales/$customerId"
                      params={{ customerId: owner.id }}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      {customerDisplayName(owner)}
                    </Link>
                    {owner.company_name && (
                      <span className="text-xs text-muted-foreground">{owner.company_name}</span>
                    )}
                  </span>
                </Row>
              )}
              <Row label="Asking price">
                <span className="font-medium">${boat.price.toLocaleString()}</span>
              </Row>
              <Row label="Cost">
                <span className="inline-flex flex-col items-end">
                  <span>{boat.cost > 0 ? `$${boat.cost.toLocaleString()}` : '—'}</span>
                  {boat.cost > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Margin: ${(boat.price - boat.cost).toLocaleString()}
                    </span>
                  )}
                </span>
              </Row>
              <Row label="Slip">
                {slipLoading ? (
                  <Skeleton className="ml-auto h-5 w-24" />
                ) : slip ? (
                  <>
                    <Link
                      to="/storage/$slipId"
                      params={{ slipId: slip.id }}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      Slip {slip.slip_number}
                    </Link>{' '}
                    <span className="text-muted-foreground">
                      ({SLIP_TYPE_LABELS[slip.type as SlipType]})
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">No slip assigned</span>
                )}
              </Row>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Specifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <Spec label="Year">{boat.year}</Spec>
                <Spec label="Length">{boat.length_ft} ft</Spec>
                <Spec label="Color">{boat.color || '—'}</Spec>
                <Spec label="HIN">
                  <span className="break-all">{boat.hin || '—'}</span>
                </Spec>
                <Spec label="Condition">
                  <span className="capitalize">{boat.condition}</span>
                </Spec>
                <Spec label="Status">{BOAT_STATUS_LABELS[boat.status]}</Spec>
                <Spec label="Listing">{LISTING_STATE_LABELS[boat.listing_state]}</Spec>
                <Spec label="Engine hours">
                  {boat.engine_hours != null ? boat.engine_hours.toLocaleString() : '—'}
                </Spec>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Engine
                  </div>
                  <div className="text-sm font-medium">{engineLabel || '—'}</div>
                  {boat.engine_count > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {boat.engine_count} engine{boat.engine_count === 1 ? '' : 's'}
                      {boat.engine_serial ? ` · SN ${boat.engine_serial}` : ''}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <BoatConsignmentCard boat={boat} />

          <BoatLoanCard boat={boat} />
        </div>
      </div>

      <ApplyPlanDialog
        boatId={boatId}
        boatName={`${boat.make} ${boat.model}`}
        suggestedManufacturer={suggestion?.suggested_manufacturer ?? boat.engine_manufacturer ?? ''}
        open={applyPlanOpen}
        onOpenChange={setApplyPlanOpen}
      />

      <Dialog open={addScheduleOpen} onOpenChange={setAddScheduleOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Service Schedule</DialogTitle>
            <DialogDescription>A single recurring schedule for this boat.</DialogDescription>
          </DialogHeader>
          <ScheduleForm
            boatId={boatId}
            onSubmit={(body) =>
              createScheduleMutation.mutate(body, {
                onSuccess: () => setAddScheduleOpen(false),
              })
            }
            isPending={createScheduleMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
